import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import { Box, Avatar, Typography } from "@mui/material";
import styles from "./styles.module.scss";
import { clsx } from "@ismael1361/utils";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark as themCode } from "react-syntax-highlighter/dist/esm/styles/prism";
// import remarkGridTable from "@adobe/remark-gridtables";
import rehypeKatex from "rehype-katex";
import rehypeStringify from "rehype-stringify";
import remarkMath from "remark-math";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import remarkGfm from "remark-gfm";
import { mdiClockAlertOutline } from "@mdi/js";
import Icon from "@mdi/react";

// ── Sanitização de Markdown incompleto durante streaming ────────

/**
 * Fecha tags Markdown que ficaram abertas durante o streaming,
 * evitando que o layout "quebre" enquanto os tokens ainda chegam.
 */
function sanitizeStreamingMarkdown(md: string): string {
	let result = md;

	// Fechar blocos de código abertos (``` sem par)
	const fenceMatches = result.match(/```/g);
	if (fenceMatches && fenceMatches.length % 2 !== 0) {
		result += "\n```";
	}

	// Fechar código inline aberto (` sem par) — só se não estiver dentro de fence
	if (!fenceMatches || fenceMatches.length % 2 === 0) {
		const backtickMatches = result.match(/(?<!`)`(?!`)/g);
		if (backtickMatches && backtickMatches.length % 2 !== 0) {
			result += "`";
		}
	}

	// Fechar negrito/itálico abertos
	const boldMatches = result.match(/\*\*/g);
	if (boldMatches && boldMatches.length % 2 !== 0) {
		result += "**";
	}

	const italicMatches = result.match(/(?<!\*)\*(?!\*)/g);
	if (italicMatches && italicMatches.length % 2 !== 0) {
		result += "*";
	}

	return result;
}

// Tipagem das Props de Mensagem
export interface MessageProps {
	id: string;
	children?: string | null; // Markdown content
	incoming?: boolean;
	outgoing?: boolean;
	avatarUrl?: string | (string | undefined)[];
	senderName?: string;
	thinking?: string;
	pending?: boolean;
	error?: string;
	/** Quando true, ativa cursor pulsante e sanitização de markdown incompleto. */
	isStreaming?: boolean;
}

const MessageInner: React.FC<MessageProps> = ({ id, children, incoming, outgoing, avatarUrl, senderName, thinking, pending, error, isStreaming }) => {
	const isIncoming = incoming || !outgoing;
	const isThinking = thinking && (!children || children.trim() === "");
	const avatarUrls = Array.isArray(avatarUrl) ? avatarUrl : [avatarUrl];

	// Sanitiza markdown durante streaming para evitar layout quebrado
	const markdownContent = useMemo(() => {
		if (!children) return children;
		return isStreaming ? sanitizeStreamingMarkdown(children) : children;
	}, [children, isStreaming]);

	return (
		<Box className={clsx(styles.messageRow, isIncoming && styles.incoming, outgoing && styles.outgoing)}>
			{/* Avatar component from MUI handles image or fallback */}
			<div className={styles.avatars}>
				{avatarUrls.map((url, index) => (
					<Avatar
						key={index}
						className={styles.avatar}
						src={url}
						alt={senderName || "User"}
					>
						{!url && (senderName ? senderName[0]?.toUpperCase() : "?")}
					</Avatar>
				))}
			</div>

			<Box className={styles.messageContent}>
				{senderName && (
					<Typography
						variant="caption"
						className={styles.senderName}
					>
						{senderName}
					</Typography>
				)}
				{isThinking && <Box className={styles.thinking}>{thinking}</Box>}
				<Box
					className={styles.bubble}
					style={{
						opacity: pending || error ? 0.3 : 1,
						display: !children || children.trim() === "" ? "none" : undefined,
					}}
				>
					<ReactMarkdown
						remarkPlugins={[remarkParse, remarkMath, remarkRehype, rehypeKatex, rehypeStringify, remarkGfm]}
						components={{
							code(props) {
								const { children, className, node, ...rest } = props;
								const match = /language-(\w+)/.exec(className || "");
								return match ? (
									<SyntaxHighlighter
										{...(rest as any)}
										PreTag={({ children, style, className, ...props }) => {
											return (
												<div
													className={clsx(styles.code, className)}
													{...props}
												>
													{children}
												</div>
											);
										}}
										children={String(children).replace(/\n$/, "")}
										language={match[1]}
										style={themCode}
									/>
								) : (
									<span className={clsx(styles.code)}>
										<code
											{...(rest as any)}
											className={className}
										>
											{children}
										</code>
									</span>
								);
							},
						}}
					>
						{markdownContent}
					</ReactMarkdown>
					{/* {isStreaming && <span className={styles.streamingCursor} />} */}
				</Box>
				{pending && <Box className={styles.pending}>Enviando...</Box>}
				{error && (
					<Box className={styles.error}>
						{(error || "Erro ao enviar") + " "}
						<Icon
							path={mdiClockAlertOutline}
							size={0.7}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export const Message = React.memo(MessageInner);
